import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Thin shadcn-style wrapper around @radix-ui/react-accordion.
 *
 * The trigger renders a right-aligned chevron that rotates 180° when the item
 * opens. The content uses the Radix-provided `--radix-accordion-content-height`
 * CSS variable together with keyframes in `index.css` for a smooth height
 * transition (`accordion-down` / `accordion-up`).
 *
 * By default each `AccordionItem` gets a bottom border to visually separate
 * stacked items — pass `className="border-none"` when you want the item to
 * inherit chrome from a surrounding `Card`.
 */
const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b", className)}
    {...props}
  />
))
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        // `min-w-0` on the Trigger itself is the fix that actually lets
        // any `truncate` inside it work. Flex items default to
        // `min-width: auto` (content-based), so without this the Trigger
        // stretches past its parent to fit its content — and no amount
        // of `flex-1 min-w-0 truncate` on a child <h3> can save it.
        "flex flex-1 min-w-0 items-center justify-between gap-3 py-4 text-left font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-primary/30 [&[data-state=open]>svg.accordion-chevron]:rotate-180",
        className
      )}
      {...props}
    >
      {children}
      {/* Chevron hides itself when the nearest ancestor container query
          reports the container is ≤ 15rem (240px). Only takes effect when a
          parent opts into container queries via Tailwind's `@container`
          utility — otherwise the container query never matches and the
          chevron stays visible everywhere else in the app. */}
      <ChevronDown className="accordion-chevron h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 @max-[15rem]:hidden" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = "AccordionTrigger"

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
